import {
  FontFaces,
  PaletteColors,
  PaletteKeyColorNames,
  SizingNames,
} from './GlobalStylesTypes.js';

export const PALETTE_TONAL_OFFSETS = [
  0, 4, 6, 10, 12, 16, 17, 20, 22, 24, 30, 40, 50, 60, 70, 78, 80, 83, 87, 88, 90, 92, 94, 95, 96,
  98, 99, 100,
] as const;

export const TOKEN_PALETTE_REF_PREFIX = '--spz-ref-palette';
export const TOKEN_TYPESCALE_PREFIX = '--spz-sys-typescale';

export const SIZING_NAMES = ['small', 'medium', 'large'] as SizingNames[];

export const PALETTE_COLOR_ROLES: PaletteKeyColorNames[] = [
  'primary',
  'primary-container',
  'on-primary',
  'on-primary-container',
  'inverse-primary',
  'secondary',
  'secondary-container',
  'on-secondary',
  'on-secondary-container',
  'tertiary',
  'tertiary-container',
  'on-tertiary',
  'on-tertiary-container',
  'error',
  'error-container',
  'on-error',
  'on-error-container',
  'warning',
  'warning-container',
  'on-warning',
  'on-warning-container',
  'info',
  'info-container',
  'on-info',
  'on-info-container',
  'success',
  'success-container',
  'on-success',
  'on-success-container',
  'surface',
  'surface-dim',
  'surface-bright',
  'surface-container-lowest',
  'surface-container-low',
  'surface-container',
  'surface-container-high',
  'surface-container-highest',
  'surface-variant',
  'on-surface',
  'on-surface-variant',
  'inverse-surface',
  'inverse-on-surface',
  'background',
  'on-background',
  'outline',
  'outline-variant',
  'shadow',
  'surface-tint',
  'scrim',
];

export const PALETTE_COLOR_MAPPING = {
  primary: '#415cce',
  secondary: '#625b71',
  tertiary: '#7d5260',
  error: '#b3261e',
  warning: '#f5a623',
  info: '#007aff',
  success: '#4cd964',
  neutral: '#000000',
  'neutral-variant': '#ffffff',
} satisfies Record<PaletteColors, string>;

export const PALETTE_COLORS = [
  'primary',
  'secondary',
  'tertiary',
  'error',
  'warning',
  'info',
  'success',
  'neutral',
  'neutral-variant',
] satisfies PaletteColors[];

export const PALETTE_THEME_SCHEMAS = ['light', 'dark'] as const;

export const PALETTE_THEME_SCHEMA_LIGHT = {
  primary: 'primary50',
  'primary-container': 'primary90',
  'on-primary': 'primary100',
  'on-primary-container': 'primary10',
  'inverse-primary': 'primary80',
  secondary: 'secondary60',
  'secondary-container': 'secondary90',
  'on-secondary': 'secondary100',
  'on-secondary-container': 'secondary10',
  tertiary: 'tertiary30',
  'tertiary-container': 'tertiary90',
  'on-tertiary': 'tertiary100',
  'on-tertiary-container': 'tertiary10',
  error: 'error40',
  'error-container': 'error90',
  'on-error': 'error100',
  'on-error-container': 'error10',
  warning: 'warning40',
  'warning-container': 'warning90',
  'on-warning': 'warning100',
  'on-warning-container': 'warning10',
  info: 'info40',
  'info-container': 'info90',
  'on-info': 'info100',
  'on-info-container': 'info10',
  success: 'success40',
  'success-container': 'success90',
  'on-success': 'success100',
  'on-success-container': 'success10',
  surface: 'neutral98',
  'surface-dim': 'neutral87',
  'surface-bright': 'neutral98',
  'surface-container-lowest': 'neutral94',
  'surface-container-low': 'neutral90',
  'surface-container': 'neutral88',
  'surface-container-high': 'neutral83',
  'surface-container-highest': 'neutral78',
  'surface-variant': 'neutral-variant98',
  'on-surface': 'neutral10',
  'on-surface-variant': 'neutral-variant20',
  'inverse-surface': 'neutral20',
  'inverse-on-surface': 'neutral80',
  background: 'neutral98',
  'on-background': 'neutral10',
  outline: 'neutral83',
  'outline-variant': 'neutral-variant80',
  shadow: 'neutral0',
  'surface-tint': 'primary40',
  scrim: 'neutral0',
} as const;

export const PALETTE_THEME_SCHEMA_DARK = {
  primary: 'primary50',
  'primary-container': 'primary30',
  'on-primary': 'primary100',
  'on-primary-container': 'primary90',
  'inverse-primary': 'primary40',
  secondary: 'secondary80',
  'secondary-container': 'secondary30',
  'on-secondary': 'secondary20',
  'on-secondary-container': 'secondary90',
  tertiary: 'tertiary80',
  'tertiary-container': 'tertiary30',
  'on-tertiary': 'tertiary20',
  'on-tertiary-container': 'tertiary90',
  error: 'error60',
  'error-container': 'error30',
  'on-error': 'error20',
  'on-error-container': 'error90',
  warning: 'warning80',
  'warning-container': 'warning30',
  'on-warning': 'warning20',
  'on-warning-container': 'warning90',
  info: 'info80',
  'info-container': 'info30',
  'on-info': 'info20',
  'on-info-container': 'info90',
  success: 'success80',
  'success-container': 'success30',
  'on-success': 'success20',
  'on-success-container': 'success90',
  surface: 'neutral6',
  'surface-dim': 'neutral6',
  'surface-bright': 'neutral24',
  'surface-container-lowest': 'neutral6',
  'surface-container-low': 'neutral10',
  'surface-container': 'neutral12',
  'surface-container-high': 'neutral17',
  'surface-container-highest': 'neutral22',
  'surface-variant': 'neutral-variant10',
  'on-surface': 'neutral90',
  'on-surface-variant': 'neutral-variant80',
  'inverse-surface': 'neutral90',
  'inverse-on-surface': 'neutral20',
  background: 'neutral6',
  'on-background': 'neutral90',
  outline: 'neutral17',
  'outline-variant': 'neutral-variant30',
  shadow: 'neutral0',
  'surface-tint': 'primary80',
  scrim: 'neutral0',
} as const;

export const TYPESCALE_MAPPING = {
  display: {
    'font-family-name': 'Roboto',
    'font-family-style': 'Regular',
    'font-weight': '400',
    'font-size': ['36px', '45px', '57px'],
    'line-height': ['44px', '52px', '64px'],
    'letter-spacing': ['0px', '0px', '-0.25px'],
  },
  headline: {
    'font-family-name': 'Roboto',
    'font-family-style': 'Regular',
    'font-weight': '400',
    'font-size': ['24px', '28px', '32px'],
    'line-height': ['32px', '36px', '40px'],
    'letter-spacing': ['0px', '0px', '0px'],
  },
  body: {
    'font-family-name': 'Roboto',
    'font-family-style': 'Regular',
    'font-weight': '400',
    'font-size': ['12px', '14px', '16px'],
    'line-height': ['16px', '20px', '24px'],
    'letter-spacing': ['0.4px', '0.25px', '0.5px'],
  },
  label: {
    'font-family-name': 'Roboto',
    'font-family-style': 'Regular',
    'font-weight': '400',
    'font-size': ['11px', '12px', '14px'],
    'line-height': ['16px', '16px', '20px'],
    'letter-spacing': ['0.5px', '0.5px', '0.1px'],
  },
  title: {
    'font-family-name': 'Roboto',
    'font-family-style': 'Regular',
    'font-weight': '400',
    'font-size': ['16px', '18px', '22px'],
    'line-height': ['20px', '24px', '28px'],
    'letter-spacing': ['0.1px', '0.15px', '0px'],
  },
};

export const createFontFaces = (fontSources: FontFaces) => {
  const {
    fontRobotoLatin100,
    fontRobotoLatin100Italic,
    fontRobotoLatin300,
    fontRobotoLatin300Italic,
    fontRobotoLatin400,
    fontRobotoLatin400Italic,
    fontRobotoLatin500,
    fontRobotoLatin500Italic,
    fontRobotoLatin700,
    fontRobotoLatin700Italic,
    fontRobotoLatin900,
    fontRobotoLatin900Italic,
  } = fontSources;

  return {
    // https://gwfh.mranftl.com/fonts/roboto?subsets=latin
    Roboto: [
      {
        url: fontRobotoLatin100,
        weight: 100,
        style: 'normal',
      },
      {
        url: fontRobotoLatin100Italic,
        weight: 100,
        style: 'italic',
      },
      {
        url: fontRobotoLatin300,
        weight: 300,
        style: 'normal',
      },
      {
        url: fontRobotoLatin300Italic,
        weight: 300,
        style: 'italic',
      },
      {
        url: fontRobotoLatin400,
        weight: 400,
        style: 'normal',
      },
      {
        url: fontRobotoLatin400Italic,
        weight: 400,
        style: 'italic',
      },
      {
        url: fontRobotoLatin500,
        weight: 500,
        style: 'normal',
      },
      {
        url: fontRobotoLatin500Italic,
        weight: 500,
        style: 'italic',
      },
      {
        url: fontRobotoLatin700,
        weight: 700,
        style: 'normal',
      },
      {
        url: fontRobotoLatin700Italic,
        weight: 700,
        style: 'italic',
      },
      {
        url: fontRobotoLatin900,
        weight: 900,
        style: 'normal',
      },
      {
        url: fontRobotoLatin900Italic,
        weight: 900,
        style: 'italic',
      },
    ],
  };
};
